import type { ClusterProvider, DatabaseType } from '@/graphql.types'

const DatabaseDriverFamily = {
  Bitnami: 'bitnami',
  Cnpg: 'cnpg',
  Pxc: 'pxc',
  MariadbOperator: 'mariadbOperator',
  RedisOperator: 'redisOperator',
  ValkeySts: 'valkeySts',
} as const

type DatabaseDriverFamily =
  (typeof DatabaseDriverFamily)[keyof typeof DatabaseDriverFamily]

const ociDriverFamilies: Partial<Record<DatabaseType, DatabaseDriverFamily>> = {
  postgresql: DatabaseDriverFamily.Cnpg,
  mysql: DatabaseDriverFamily.Pxc,
  mariadb: DatabaseDriverFamily.MariadbOperator,
  redis: DatabaseDriverFamily.RedisOperator,
  valkey: DatabaseDriverFamily.ValkeySts,
}

const bitnamiVersions: Partial<Record<DatabaseType, string[]>> = {
  postgresql: ['17', '16'],
  mysql: ['9.0', '8.0'],
  mariadb: ['11.1', '11.0', '10.11', '10.6', '10.5', '10.4'],
  redis: ['8.x', '7.x', '6.x', '5.0'],
  valkey: ['7.2'],
}

const versionsByDriverFamily: Record<
  DatabaseDriverFamily,
  Partial<Record<DatabaseType, string[]>>
> = {
  [DatabaseDriverFamily.Bitnami]: bitnamiVersions,
  [DatabaseDriverFamily.Cnpg]: {
    postgresql: ['18', '17', '16'],
  },
  [DatabaseDriverFamily.Pxc]: {
    mysql: ['8.0'],
  },
  [DatabaseDriverFamily.MariadbOperator]: {
    mariadb: ['11.8', '11.4', '10.11'],
  },
  [DatabaseDriverFamily.RedisOperator]: {
    redis: ['8.6', '8.2', '7.2'],
  },
  [DatabaseDriverFamily.ValkeySts]: {
    valkey: ['9.0', '8.1', '7.2'],
  },
}

// Versions that were offered on create before the matrix was narrowed. Databases
// created with them are valid, so reporting keeps treating them as known.
const retiredVersionsByDriverFamily: Partial<
  Record<DatabaseDriverFamily, Partial<Record<DatabaseType, string[]>>>
> = {
  [DatabaseDriverFamily.Bitnami]: {
    postgresql: ['15', '14', '13', '12', '11', '10', '9.6'],
  },
  [DatabaseDriverFamily.Cnpg]: {
    postgresql: ['15', '14', '13'],
  },
  [DatabaseDriverFamily.Pxc]: {
    mysql: ['8.4'],
  },
}

type DriverFamilyLookup = {
  type: DatabaseType
  provider?: ClusterProvider
}

const getDriverFamily = ({
  type,
  provider,
}: DriverFamilyLookup): DatabaseDriverFamily => {
  if (provider !== 'oci') {
    return DatabaseDriverFamily.Bitnami
  }

  return ociDriverFamilies[type] ?? DatabaseDriverFamily.Bitnami
}

// Every OCI family now ships its own matrix. A family that carries no entry for a
// type falls back to the Bitnami list, mirroring the backend, which leaves those
// creates unrestricted rather than rejecting them.
export const getDatabaseVersions = ({
  type,
  provider,
}: DriverFamilyLookup): string[] =>
  versionsByDriverFamily[getDriverFamily({ type, provider })][type] ??
  bitnamiVersions[type] ??
  []

type VersionSupportLookup = DriverFamilyLookup & {
  version: string
}

export const isDatabaseVersionSupported = ({
  type,
  provider,
  version,
}: VersionSupportLookup): boolean =>
  getDatabaseVersions({ type, provider }).includes(version)

type KnownVersionLookup = {
  type: string
  driverFamily: string
  version: string
}

const versionsByDriverFamilyName: Record<
  string,
  Partial<Record<string, string[]>>
> = versionsByDriverFamily

const retiredVersionsByDriverFamilyName: Partial<
  Record<string, Partial<Record<string, string[]>>>
> = retiredVersionsByDriverFamily

const getMajor = (version: string): string => version.split('.')[0]

// Bitnami rows go back to before the backend validated versions on create, so the
// same release can be written in looser forms than the matrix entry it belongs to:
// a major on its own ("9" for "9.0") or a concrete minor under a wildcard entry
// ("7.2" for "7.x").
const matchesBitnamiVersion = (
  version: string,
  supportedVersion: string
): boolean => {
  if (version === supportedVersion) {
    return true
  }

  if (supportedVersion.endsWith('.x')) {
    return getMajor(version) === getMajor(supportedVersion)
  }

  return supportedVersion.startsWith(`${version}.`)
}

// Checks a stored version against the matrix of the family it was created with,
// for reporting on raw backend values rather than driving a create form. A family
// or type the matrix doesn't cover counts as known, so unmapped combinations are
// never reported as bad data.
export const isKnownDatabaseVersion = ({
  type,
  driverFamily,
  version,
}: KnownVersionLookup): boolean => {
  const versions = versionsByDriverFamilyName[driverFamily]?.[type]

  if (!versions) {
    return true
  }

  const retiredVersions =
    retiredVersionsByDriverFamilyName[driverFamily]?.[type] ?? []
  const knownVersions = [...versions, ...retiredVersions]
  const isBitnami = driverFamily === DatabaseDriverFamily.Bitnami

  if (!isBitnami) {
    return knownVersions.includes(version)
  }

  return knownVersions.some((supportedVersion) =>
    matchesBitnamiVersion(version, supportedVersion)
  )
}
